import { useSearchParams } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { Box, Breadcrumbs, Tab, Tabs, Typography } from '@mui/material'
import { Home as HomeIcon } from '@mui/icons-material'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { EditModeToolbar } from '@/components/dashboard/EditModeToolbar'
import { RoleDashboard } from '@/components/dashboard/RoleDashboard'
import type { DashboardRole } from '@/types/dashboard'
import '@/styles/dashboard.css'

/**
 * `/dashboard`: сетка виджетов по роли.
 * - teacher: дашборд учителя; student: дашборд ученика
 * - teacher+student: переключение через `?tab=teaching|student` (по умолчанию student)
 * - без ролей дашборда (чистый admin): только приветствие
 */
export function DashboardPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useAuthStore((state) => state.user)

  const roles = user?.roles ?? []
  const isStudent = roles.some((r) => r.slug === 'student')
  const isTeacher = roles.some((r) => r.slug === 'teacher')
  const isDual = isStudent && isTeacher
  const tab = searchParams.get('tab')

  let role: DashboardRole | null = null
  if (isDual) {
    role = tab === 'teaching' ? 'teacher' : 'student'
  } else if (isTeacher) {
    role = 'teacher'
  } else if (isStudent) {
    role = 'student'
  }

  const getUserDisplayName = () => {
    if (user?.name) return user.name
    if (user?.first_name) {
      const parts = [user.first_name, user.surname].filter(Boolean)
      if (parts.length > 0) return parts.join(' ')
    }
    if (user?.email) return user.email
    return t('dashboard.user')
  }

  return (
    <DashboardLayout fullWidth>
      {/* Горизонтальный паддинг даёт .dashboard-main (2rem) — без дублирования */}
      <Box sx={{ px: 0, py: { xs: 1, sm: 1.5 } }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1,
          }}
        >
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('dashboard.welcome')}
          </Typography>
          <PageHelpPopover pageKey="dashboard" />
          {role !== null && (
            <Box sx={{ ml: 'auto' }}>
              <EditModeToolbar />
            </Box>
          )}
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t('dashboard.welcomeMessage', { name: getUserDisplayName() })}
        </Typography>

        {isDual && (
          <Tabs
            value={role === 'teacher' ? 'teaching' : 'student'}
            onChange={(_, value: string) => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev)
                if (value === 'teaching') {
                  next.set('tab', 'teaching')
                } else {
                  next.delete('tab')
                }
                return next
              })
            }}
            sx={{ mb: 2 }}
          >
            <Tab value="student" label={t('dashboard.tabs.student')} />
            <Tab value="teaching" label={t('dashboard.tabs.teaching')} />
          </Tabs>
        )}

        {role !== null && <RoleDashboard role={role} />}
      </Box>
    </DashboardLayout>
  )
}
